import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Calendar, Image as ImageIcon, Save, X } from 'lucide-react';
import SimpleRichEditor from './SummerNoteEditor';
import PhotoAlbumUploader from './PhotoAlbumUploader';
import '../../summernote-fix.css';

const NewsManagement = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [uploading, setUploading] = useState(false);

  // États du formulaire
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    cover_image: '',
    photo_album: [],
    published_at: '',
    is_draft: false  // Mode brouillon
  });

  // Charger les articles
  const loadArticles = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/news`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setArticles(data.news || []);
      }
    } catch (error) {
      console.error('Erreur chargement articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArticles();
  }, []);

  // Gestion du contenu SummerNote
  const handleContentChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
  };

  // Compression d'image avant upload
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionner si nécessaire
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convertir en blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Échec de la compression'));
              }
            },
            file.type,
            quality
          );
        };
        img.onerror = () => reject(new Error('Échec du chargement de l\'image'));
      };
      reader.onerror = () => reject(new Error('Échec de la lecture du fichier'));
    });
  };

  // Upload d'image avec compression
  const handleImageUpload = async (file) => {
    setUploading(true);
    console.log('📤 Début upload image:', file.name, 'Taille originale:', (file.size / 1024).toFixed(2), 'KB');

    try {
      // Compresser l'image avant upload
      const compressedBlob = await compressImage(file, 1200, 0.8);
      console.log('🗜️ Image compressée:', (compressedBlob.size / 1024).toFixed(2), 'KB');

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedBlob, file.name);

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Réponse upload:', data);
        // Construire l'URL complète pour l'image
        const fullImageUrl = `${process.env.REACT_APP_BACKEND_URL}${data.file_url}`;
        console.log('🖼️ URL complète de l\'image:', fullImageUrl);
        setFormData(prev => ({ ...prev, cover_image: fullImageUrl }));
        alert('Image uploadée avec succès !');
      } else {
        const errorData = await response.text();
        console.error('❌ Erreur response:', response.status, errorData);
        alert(`Erreur lors de l'upload: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('❌ Erreur upload:', error);
      alert(`Erreur lors de l'upload: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('Titre et contenu sont requis');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const method = editingArticle ? 'PUT' : 'POST';
      const url = editingArticle 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/news/${editingArticle.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/news`;

      // Préparer les données - Si pas de published_at, utiliser la date actuelle
      const articleData = {
        title: formData.title,
        content: formData.content,
        cover_image: formData.cover_image || null,
        photo_album: formData.photo_album || [],
        published_at: formData.published_at ? new Date(formData.published_at).toISOString() : new Date().toISOString(),
        is_draft: formData.is_draft || false  // Mode brouillon
      };

      console.log('📤 Envoi article:', articleData);

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(articleData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Article sauvegardé:', data);
        await loadArticles();
        setShowForm(false);
        setEditingArticle(null);
        setFormData({ title: '', content: '', cover_image: '', photo_album: [], published_at: '', is_draft: false });
        alert(editingArticle ? 'Article mis à jour !' : 'Article créé !');
      } else {
        const errorData = await response.text();
        console.error('❌ Erreur response:', response.status, errorData);
        alert(`Erreur lors de la sauvegarde: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert(`Erreur lors de la sauvegarde: ${error.message}`);
    }
  };

  // Supprimer un article
  const handleDelete = async (articleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/news/${articleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await loadArticles();
        alert('Article supprimé !');
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Éditer un article
  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      content: article.content,
      cover_image: article.cover_image || '',
      photo_album: article.photo_album || [],
      published_at: article.published_at ? new Date(article.published_at).toISOString().slice(0, 16) : '',
      is_draft: article.is_draft || false  // Mode brouillon
    });
    setShowForm(true);
  };

  // Annuler
  const handleCancel = () => {
    setShowForm(false);
    setEditingArticle(null);
    setFormData({ title: '', content: '', cover_image: '', photo_album: [], published_at: '', is_draft: false });
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des News/Blog</h1>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Nouvelle Publication
          </button>
        )}
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              {editingArticle ? 'Modifier l\'article' : 'Nouvel Article'}
            </h2>
            <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700">
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titre *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Upload d'image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0])}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                >
                  <ImageIcon className="h-4 w-4" />
                  {uploading ? 'Upload en cours...' : 'Choisir une image'}
                </label>
                {formData.cover_image && (
                  <div className="flex items-center gap-2">
                    <img src={formData.cover_image} alt="Preview" className="h-12 w-12 object-cover rounded" />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, cover_image: '' }))}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Contenu - SummerNote */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contenu *
              </label>
              <SimpleRichEditor
                value={formData.content}
                onChange={handleContentChange}
                placeholder="Rédigez votre article..."
              />
            </div>

            {/* Album Photo */}
            <div>
              <PhotoAlbumUploader
                photos={formData.photo_album}
                onPhotosChange={(photos) => setFormData(prev => ({ ...prev, photo_album: photos }))}
                maxPhotos={36}
              />
            </div>

            {/* Date de publication */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="inline h-4 w-4 mr-1" />
                Date de publication
              </label>
              <input
                type="datetime-local"
                value={formData.published_at}
                onChange={(e) => setFormData(prev => ({ ...prev, published_at: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-gray-600 mt-1">
                L'article sera publié automatiquement à cette date (si non brouillon).
              </p>
            </div>

            {/* Mode brouillon */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_draft}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_draft: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  📝 Mode brouillon (ne pas publier)
                </span>
              </label>
              <p className="text-sm text-gray-600 mt-1 ml-6">
                Si coché, l'article ne sera pas publié même si la date est passée.
              </p>
            </div>

            {/* Boutons */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {editingArticle ? 'Mettre à jour' : 'Publier'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des articles */}
      {!showForm && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Articles ({articles.length})</h2>
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>Aucun article trouvé</p>
              <p className="text-sm mt-1">Cliquez sur "Nouvelle Publication" pour créer votre premier article</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {articles.map((article) => {
                const isPublished = article.is_published && article.published_at && new Date(article.published_at) <= new Date() && !article.is_draft;
                const isScheduled = !article.is_published && article.published_at && new Date(article.published_at) > new Date() && !article.is_draft;
                const isDraft = article.is_draft;
                
                return (
                  <div key={article.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-medium text-gray-900">{article.title}</h3>
                          <div className="flex gap-2">
                            {isDraft && (
                              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                                📝 Brouillon
                              </span>
                            )}
                            {isPublished && (
                              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                                Publié
                              </span>
                            )}
                            {isScheduled && (
                              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                                Programmé
                              </span>
                            )}
                            {!article.published_at && (
                              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                                Brouillon
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-gray-600 text-sm mb-2" dangerouslySetInnerHTML={{ 
                          __html: article.content.replace(/<[^>]*>/g, '').substring(0, 150) + '...' 
                        }}></p>
                        
                        <div className="text-xs text-gray-500">
                          {article.published_at ? (
                            <span>Publication: {new Date(article.published_at).toLocaleString('fr-FR')}</span>
                          ) : (
                            <span>Créé le {new Date(article.created_at).toLocaleString('fr-FR')}</span>
                          )}
                        </div>
                      </div>

                      {article.cover_image && (
                        <img 
                          src={article.cover_image} 
                          alt={article.title}
                          className="w-20 h-20 object-cover rounded ml-4"
                        />
                      )}
                    </div>

                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => handleEdit(article)}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Edit className="h-4 w-4" />
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(article.id)}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                        Supprimer
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsManagement;